// ============================================
// FichaExpediente.js — Vista de detalle pantalla completa
// ============================================
// Replica el patron visual de FichaGrupo / FichaRazonSocial:
//   - useState/useEffect que carga GET /api/oportunidades/:id
//   - Header con boton volver + folio + badges
//   - Botones de accion segun rol (direccion vs resto)
//   - 7 tabs (solo Resumen y Vinculados con contenido real)
//   - Modales para cierre, edicion, reapertura, borrado, vinculacion, reasignacion
//
// FASE 2B PARTE B.3: tabs Contactos / Cotizaciones / Servicios / Equipos /
// Historial son placeholders "Proximamente". Se implementan en fases futuras.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useUsuario } from '../../context/UsuarioContext';
// PORTADA-EXP: caratula imprimible del folder fisico (PDF 1 pagina + QR).
import { Printer, MoreVertical, AlertTriangle, Undo2, XCircle, Archive, Trash2, FileText } from 'lucide-react';
import { QRCodeCanvas } from 'qrcode.react';
import { generarPortadaExpedientePDF } from '../../pdf/portadaExpedientePDF';
// PANEL-NAVEGADOR (2026-07-10): el contexto del proyecto vive DENTRO del
// panel unico de PanelFlujo (paso 1); aqui solo se navega hacia el.
import {
  API_BASE_CLI, FF,
  COLOR_BEEP_AZUL, COLOR_OSCURO, COLOR_FONDO, COLOR_BORDE, COLOR_GRIS, COLOR_GRIS_CL,
  COLOR_OK, COLOR_PELIGRO,
  ESTADO_VISUAL_OPORTUNIDAD, TIPO_EXPEDIENTE_VISUAL, CIERRE_VISUAL,
  authHeaders, esRolDireccion, fmtMoneda, fmtFecha, etiquetaAutor,
} from './helpersExpedientes';
import { puedeVerFinanzas } from '../../utils/permisos';
import ModalCerrarExpediente from './ModalCerrarExpediente';
// COTIZAR-RAPIDO (2026-09-23): cotizar sin salir del expediente.
import PanelCotizarRapido from './PanelCotizarRapido';
import CotizacionesParecidas from './CotizacionesParecidas';
import ModalEditarExpediente from './ModalEditarExpediente';
import { textoUbicacionLibre } from '../../utils/ubicacionExpediente';
import useFamilias, { etiquetaFamilia } from '../../hooks/useFamilias';
import ModalReabrirExpediente from './ModalReabrirExpediente';
import ModalBorrarPermanente from './ModalBorrarPermanente';
import ModalCrearHijo from './ModalCrearHijo';
import ModalReasignar from './ModalReasignar';
import ModalVincularCliente from './ModalVincularCliente';
import CotizacionesLista from '../CotizacionesLista';
import GarantiasLista from '../GarantiasLista';
import PedirDatosCard from '../PedirDatosCard';
// FLUJOS-FIJOS (Entrega 1): vista de paso del expediente (stepper + requisito
// + boton de accion). Vive arriba del tab Resumen.
import PanelFlujo from './PanelFlujo';
// CONTRATISTAS-EXTERNOS Bloque 3: la pestaña Servicios = los trabajos programados del expediente (misma data del calendario).
import ServiciosExpediente from './ServiciosExpediente';
import { URL_APP } from '../../config/api';
import TabMaterial from './TabMaterial';
// ALMACEN v3 Parte B (2026-10-21): mapa de puntos, consumos/conteo, verificacion de garantia, cuadre y acta de entrega.
import TabPuntos from './TabPuntos';

const TABS = [
  // TAREAS-EXPEDIENTE (2026-07-10): al abrir un expediente se entra DIRECTO a
  // Tareas (flujo activo + trabajo del paso actual). Resumen sigue disponible.
  { id: 'tareas',      label: 'Tareas' },
  { id: 'resumen',     label: 'Resumen' },
  { id: 'contactos',   label: 'Contactos' },
  { id: 'cotizaciones',label: 'Cotizaciones' },
  // COMPRAS Y COSTO REAL (2026-10-17): material del expediente con estado surtido/comprado/falta y,
  // para direccion/compras, costo congelado vs real y margen real. Visible para todos (sin cifras).
  { id: 'material',    label: 'Material' },
  { id: 'servicios',   label: 'Servicios' },
  // AUDITOR (2026-10-21): antes una maqueta "Equipos instalados"; ahora el MAPA DE PUNTOS real (punto -> serie -> fotos).
  { id: 'equipos',     label: 'Puntos' },
  { id: 'vinculados',  label: 'Vinculados' },
  { id: 'garantias',   label: 'Garantías' },
  { id: 'historial',   label: 'Historial' },
];

export default function FichaExpediente({ id, onVolver, onCambiarId, esCelular }) {
  const { usuario } = useUsuario();
  const direccion = esRolDireccion(usuario);
  // CALENDARIO-E1: el tecnico (sin acceso financiero) no ve la pestaña
  // Cotizaciones ni montos. El backend ya lo hace cumplir (403 + cotizaciones []).
  const verFinanzas = puedeVerFinanzas(usuario);
  const TABS_VISIBLES = verFinanzas ? TABS : TABS.filter(t => t.id !== 'cotizaciones');

  // ===== STATE =====
  const [expediente, setExpediente]   = useState(null);
  // AUDITOR: el paso Ejecucion manda a la pestana Puntos con un evento (el flujo no conoce las pestanas).
  useEffect(() => { const h = (e) => { if (e.detail && e.detail.tab) setTab(e.detail.tab); }; window.addEventListener('beep-expediente-tab', h); return () => window.removeEventListener('beep-expediente-tab', h); }, []);
  const [historial, setHistorial]     = useState([]);
  const [eventos, setEventos]         = useState([]); // reasignaciones + ediciones (trazabilidad)
  const [recordatorios, setRecordatorios] = useState([]);
  const [cotizaciones, setCotizaciones] = useState([]);
  const [hijos, setHijos]             = useState([]);
  const [cargando, setCargando]       = useState(true);
  const [error, setError]             = useState(null);
  const [tab, setTab]                 = useState('tareas');

  // Modales (un boolean por tipo, mas tipoCierre cuando aplica)
  const [modalCerrar,     setModalCerrar]     = useState(null); // 'ganado' | 'perdido' | 'descartado' | null
  const [modalEditar,     setModalEditar]     = useState(false);
  // COTIZAR-RAPIDO: el panel de tres toques. Se abre desde la cabecera, desde la
  // tarjeta del resumen y desde el paso de Cotizacion: los tres sitios donde la
  // gente ya esta mirando cuando se da cuenta de que hay que cotizar.
  const [panelCotizar,    setPanelCotizar]    = useState(false);
  const [modalReabrir,    setModalReabrir]    = useState(false);
  const [modalBorrar,     setModalBorrar]     = useState(false);
  const [modalCrearHijo,  setModalCrearHijo]  = useState(false);
  const [modalReasignar,  setModalReasignar]  = useState(false);
  const [modalVincular,   setModalVincular]   = useState(false);
  // PANEL-NAVEGADOR: peticion de navegar el panel de Tareas a un paso (ej.
  // "Editar contexto" desde la card de Resumen -> paso 1 en modo edicion).
  const [verPasoReq, setVerPasoReq] = useState(null); // { codigo, n }

  const abrirContexto = () => {
    setTab('tareas');
    setVerPasoReq({ codigo: 'nuevo_interes', n: Date.now() });
  };

  // PORTADA-EXP: generacion del PDF de la caratula. El QR se renderiza oculto
  // con qrcode.react (ya instalada) y se lee como dataURL al momento de imprimir.
  const [imprimiendo, setImprimiendo] = useState(false);
  const qrWrapRef = useRef(null);
  // Alimenta el QR de la portada del expediente IMPRESA: direccion publica, no la del navegador.
  const urlExpediente = `${URL_APP}/?exp=${id}`;

  // ===== EFFECTS =====
  // PANEL-NAVEGADOR (fix Don Benito 2026-07-10): los REFETCHES son silenciosos.
  // setCargando(true) solo en la carga inicial o al cambiar de expediente —
  // si no, el placeholder "Cargando..." desmonta TODO el arbol (PanelFlujo
  // incluido), se pierde el paso que se estaba viendo y el panel brinca al
  // paso actual tras editar un paso completado.
  const idCargadoRef = useRef(null);
  const cargarExpediente = useCallback(async () => {
    if (idCargadoRef.current !== id) setCargando(true);
    setError(null);
    try {
      const r = await fetch(`${API_BASE_CLI}/api/oportunidades/${id}`, { headers: authHeaders() }).then(x => x.json());
      if (r?.error) throw new Error(r.error);
      idCargadoRef.current = id;
      setExpediente(r.oportunidad);
      setHistorial(Array.isArray(r.historial) ? r.historial : []);
      setEventos(Array.isArray(r.eventos) ? r.eventos : []);
      setRecordatorios(Array.isArray(r.recordatorios) ? r.recordatorios : []);
      setCotizaciones(Array.isArray(r.cotizaciones) ? r.cotizaciones : []);
    } catch (e) {
      setError(e.message || 'No se pudo cargar el expediente');
    } finally {
      setCargando(false);
    }
  }, [id]);

  useEffect(() => { cargarExpediente(); }, [cargarExpediente]);

  // Cargar hijos cuando entra al tab Vinculados (o al cambiar id)
  const cargarHijos = useCallback(async () => {
    try {
      const r = await fetch(`${API_BASE_CLI}/api/oportunidades?tipo=todos&padre_id=${id}`, { headers: authHeaders() })
        .then(x => x.json());
      setHijos(Array.isArray(r) ? r : []);
    } catch { setHijos([]); }
  }, [id]);

  useEffect(() => {
    if (tab === 'vinculados' || (expediente && expediente.total_hijos > 0)) {
      cargarHijos();
    }
  }, [tab, expediente, cargarHijos]);

  // ===== DERIVADOS =====
  const estadoVisual = useMemo(() => (
    expediente ? (ESTADO_VISUAL_OPORTUNIDAD[expediente.estado] || null) : null
  ), [expediente]);

  const tipoExpVisual = useMemo(() => (
    expediente && expediente.tipo_expediente && expediente.tipo_expediente !== 'normal'
      ? TIPO_EXPEDIENTE_VISUAL[expediente.tipo_expediente]
      : null
  ), [expediente]);

  const cierreVisual = useMemo(() => (
    expediente?.tipo_cierre ? CIERRE_VISUAL[expediente.tipo_cierre] : null
  ), [expediente]);

  const cliente = useMemo(() => {
    if (!expediente) return '';
    const tieneIDs = !!(
      expediente.cliente_id || expediente.cliente_grupo_id ||
      expediente.cliente_razon_social_id || expediente.cliente_sucursal_id ||
      expediente.cliente_persona_id
    );
    return expediente.cliente_jerarquia_corta
      || expediente.cliente_nombre
      || expediente.contacto_nombre
      || (tieneIDs ? 'Cliente vinculado' : 'Sin cliente');
  }, [expediente]);

  // MULTI-INTERES (2026-10-17): los rubros del expediente (varios), con el principal primero.
  // Si no tiene ninguno marcado, se enseña la necesidad vieja como hasta ahora.
  const familiasCat = useFamilias();
  const rubrosTexto = useMemo(() => {
    const lista = (expediente && Array.isArray(expediente.familias) && expediente.familias.length)
      ? expediente.familias : (expediente && expediente.familia ? [expediente.familia] : []);
    return lista.map(id => etiquetaFamilia(id, familiasCat) || id).join(' + ');
  }, [expediente, familiasCat]);
  const subtitulo = useMemo(() => {
    if (!expediente) return '';
    const partes = [];
    if (rubrosTexto) partes.push(rubrosTexto);
    else if (expediente.necesidad_nombre) partes.push(`${expediente.necesidad_icono || ''} ${expediente.necesidad_nombre}`.trim());
    if (expediente.tipo_lugar_nombre) partes.push(`${expediente.tipo_lugar_icono || ''} ${expediente.tipo_lugar_nombre}`.trim());
    return partes.join('  ·  ');
  }, [expediente, rubrosTexto]);

  // ===== HANDLERS =====
  const tras_modal = (refetch = true) => {
    setModalCerrar(null);
    setModalEditar(false);
    setModalReabrir(false);
    setModalCrearHijo(false);
    setModalReasignar(false);
    setModalVincular(false);
    if (refetch) {
      cargarExpediente();
      cargarHijos();
    }
  };
  const tras_borrado = () => {
    setModalBorrar(false);
    onVolver(); // vuelve a la lista
  };

  // PORTADA-EXP: genera el PDF de la caratula (1 pagina carta) y registra la
  // impresion en la trazabilidad (best-effort: si el registro falla, el PDF
  // ya se descargo y no se molesta al usuario).
  const imprimirPortada = async () => {
    if (imprimiendo || !expediente) return;
    setImprimiendo(true);
    try {
      // Pasos del flujo REAL de este expediente (11 u 9 segun el tipo).
      const f = await fetch(`${API_BASE_CLI}/api/oportunidades/${id}/flujo`, { headers: authHeaders() })
        .then(x => x.json());
      const pasosFlujo = (f && f.ok && Array.isArray(f.pasos)) ? f.pasos : [];
      // Fecha/responsable REALES de los pasos hechos, desde el historial (DESC):
      // preferimos la fila que SALIO del paso (cuando se completo y quien lo
      // avanzo); si no existe (p.ej. salto multiple), la fila que ENTRO al paso.
      const pasos = pasosFlujo.map(p => {
        let fecha = null, responsable = null;
        if (p.situacion === 'hecho') {
          const fila = historial.find(h => h.estado_anterior === p.codigo)
            || historial.find(h => h.estado_nuevo === p.codigo);
          if (fila) { fecha = fila.fecha; responsable = fila.usuario_nombre || null; }
        }
        return { nombre: p.nombre, situacion: p.situacion, fecha, responsable };
      });
      const canvasQR = qrWrapRef.current ? qrWrapRef.current.querySelector('canvas') : null;
      await generarPortadaExpedientePDF({
        folio: expediente.folio,
        titulo: expediente.titulo || 'Sin titulo',
        clienteTexto: cliente,
        ubicacionTexto: expediente.cliente_sucursal_nombre || textoUbicacionLibre(expediente) || '',
        tipoTrabajo: (f && f.ok && f.workflow && f.workflow.nombre) || '—',
        responsable: expediente.responsable_nombre || 'Sin responsable',
        fechaInicio: expediente.fecha_interes || expediente.creado_at,
        cotFolio: (f && f.ok && f.cotizacion && f.cotizacion.folio) || null, // SOLO folio, sin monto
        pasos,
        qrDataUrl: canvasQR ? canvasQR.toDataURL('image/png') : null,
        impresoPor: (usuario && usuario.nombre) || '',
      });
      // Trazabilidad: quien imprimio y cuando (aparece en el tab Historial).
      fetch(`${API_BASE_CLI}/api/oportunidades/${id}/portada-impresa`, {
        method: 'POST', headers: { ...authHeaders() },
      }).then(() => cargarExpediente()).catch(() => {});
    } catch (e) {
      console.error('[imprimirPortada]', e);
      window.alert('No se pudo generar la portada. Intenta de nuevo.');
    } finally {
      setImprimiendo(false);
    }
  };
  const navegarA = (otroId) => {
    if (typeof onCambiarId === 'function') onCambiarId(otroId);
  };

  // ===== RENDER =====
  if (cargando) {
    return (
      <div style={{
        fontFamily: FF, padding: 40, textAlign: 'center',
        color: COLOR_GRIS_CL, background: COLOR_FONDO, minHeight: '100%',
      }}>Cargando expediente...</div>
    );
  }
  if (error || !expediente) {
    return (
      <div style={{
        fontFamily: FF, padding: 24, background: COLOR_FONDO, minHeight: '100%',
      }}>
        <button onClick={onVolver} style={btnVolver}>{'←'} Volver</button>
        <div style={{
          marginTop: 18,
          background: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c',
          padding: 16, borderRadius: 10, fontSize: 13,
        }}>{error || 'Expediente no encontrado.'}</div>
      </div>
    );
  }

  const cerrado = expediente.tipo_cierre != null;
  const dias = Number(expediente.dias_en_estado || 0);
  const mostrarAlertaDias = !cerrado && dias > 3;

  return (
    <div style={{
      fontFamily: FF, background: COLOR_FONDO, minHeight: '100%', color: COLOR_GRIS,
    }}>
      {/* PORTADA-EXP: QR oculto (canvas). Se lee como dataURL al imprimir la
          portada; apunta al deep-link del expediente (?exp=id). No es visible. */}
      <div ref={qrWrapRef} aria-hidden="true" style={{ position: 'absolute', left: -9999, top: 0, width: 0, height: 0, overflow: 'hidden' }}>
        <QRCodeCanvas value={urlExpediente} size={512} marginSize={1} />
      </div>
      {/* HEADER */}
      <div style={{
        background: '#fff', borderBottom: `1px solid ${COLOR_BORDE}`,
        padding: esCelular ? '14px 16px' : '18px 28px',
      }}>
        <button onClick={onVolver} style={btnVolver}>{'←'} Volver</button>

        <div style={{
          marginTop: 12, display: 'flex',
          alignItems: 'flex-start', justifyContent: 'space-between',
          gap: 16, flexWrap: 'wrap',
        }}>
          {/* minWidth 220 (no 0): sin piso, el titulo se comprime infinito y el
              flexWrap del padre nunca dispara — en tablet vertical la fila de 5
              botones lo dejaba en una columna ilegible (hallazgo Dona Frida
              2026-07-09). Con piso, las acciones bajan a su propia fila. */}
          <div style={{ flex: 1, minWidth: 220 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <span style={{
                fontFamily: 'ui-monospace, "SF Mono", Menlo, monospace',
                fontSize: 13, fontWeight: 700, color: COLOR_GRIS,
              }}>{expediente.folio}</span>

              {estadoVisual && (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ fontSize: 11, fontWeight: 600, color: COLOR_GRIS_CL }}>Expediente:</span>
                  <Badge color={estadoVisual.color} label={estadoVisual.label}
                         tooltip={`Estado del EXPEDIENTE (funnel)\n${estadoVisual.label}\n${estadoVisual.descripcion}`} />
                </span>
              )}
              {tipoExpVisual && (
                <Badge color={tipoExpVisual.color} label={`${tipoExpVisual.icono} ${tipoExpVisual.label}`}
                       tooltip={`${tipoExpVisual.label}\n${tipoExpVisual.descripcion}`} />
              )}
              {cierreVisual && (
                <Badge color={cierreVisual.color} label={cierreVisual.label}
                       tooltip={`Cerrado como ${cierreVisual.label}`} />
              )}
              {mostrarAlertaDias && (
                <Badge color={COLOR_PELIGRO} label={`${dias}d sin movimiento`}
                       tooltip={`Sin actualizar desde hace ${dias} dias`} />
              )}
            </div>
            {/* EXP-TITULO: jerarquia de cabecera:
                  Linea 1: titulo (grande y bold; placeholder gris si NULL)
                  Linea 2: cliente (mediano)
                  Linea 3: subtitulo (necesidad + tipo lugar) */}
            <h1 style={{
              margin: '8px 0 4px', color: expediente.titulo ? COLOR_OSCURO : COLOR_GRIS_CL,
              fontSize: esCelular ? 20 : 24, fontWeight: 700, lineHeight: 1.2,
              fontStyle: expediente.titulo ? 'normal' : 'italic',
            }}>{expediente.titulo || 'Sin titulo'}</h1>
            <div style={{
              fontSize: esCelular ? 14 : 15, color: COLOR_GRIS, fontWeight: 600,
              lineHeight: 1.3, marginBottom: subtitulo ? 4 : 0,
            }}>{cliente}</div>
            {subtitulo && (
              <div style={{ fontSize: 13, color: COLOR_GRIS, lineHeight: 1.4 }}>{subtitulo}</div>
            )}
            {/* CREADO-POR (2026-07-11): quien abrio el expediente, siempre a la
                vista (transparencia). Legacy sin creador registrado: se omite. */}
            {expediente.creado_por_nombre && (
              <div style={{ fontSize: 12, color: COLOR_GRIS_CL, marginTop: 4 }}>
                Creado por <strong style={{ color: COLOR_GRIS }}>{expediente.creado_por_nombre}</strong>
                {expediente.creado_at ? ` · ${fmtFecha(expediente.creado_at)}` : ''}
              </div>
            )}
          </div>

          {/* CABECERA-LIMPIA (2026-07-10): quedan Imprimir portada + Editar +
              menu contextual ⋮. "Cerrar ganado" YA NO existe como accion
              manual (el flujo completo cierra ganado solo); perdido/descartar/
              reabrir/borrar viven discretos en el menu (mismos candados de
              direccion y mismos modales con motivo firmado). */}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-start' }}>
            {/* PORTADA-EXP: caratula imprimible del folder. Visible para CUALQUIER
                usuario con acceso al expediente (no es accion de dinero). */}
            <button
              onClick={imprimirPortada}
              disabled={imprimiendo}
              title="Genera el PDF de la caratula fisica del folder"
              style={{
                ...btnAccion('#475569'),
                display: 'inline-flex', alignItems: 'center', gap: 6,
                opacity: imprimiendo ? 0.6 : 1,
              }}
            >
              <Printer size={15} strokeWidth={2.2} />
              {imprimiendo ? 'Generando...' : 'Imprimir portada'}
            </button>
            {/* COTIZAR-RAPIDO: la accion que antes costaba 10 clics y 5 pantallas.
                 Solo para quien ve dinero (mismo candado que el tab de cotizaciones) y
                 solo si el expediente sigue abierto: cotizar uno cerrado no tiene sentido. */}
            {verFinanzas && !cerrado && (
              <button
                onClick={() => setPanelCotizar(true)}
                title="Crear una cotizacion ligada a este expediente"
                style={{
                  ...btnAccion('#1b9e6d'),
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                }}
              >
                <FileText size={15} strokeWidth={2.2} />
                Cotizar
              </button>
            )}
            <button onClick={() => setModalEditar(true)} style={btnAccion(COLOR_BEEP_AZUL)}>
              Editar
            </button>
            <MenuAcciones
              direccion={direccion}
              cerrado={cerrado}
              tipoCierre={expediente.tipo_cierre || null}
              onCerrarPerdido={() => setModalCerrar('perdido')}
              onDescartar={() => setModalCerrar('descartado')}
              onReabrir={() => setModalReabrir(true)}
              onBorrar={() => setModalBorrar(true)}
            />
          </div>
        </div>

        {/* SUGERENCIA (nunca cierre automatico): si TODAS las cotizaciones del
            expediente estan rechazadas o vencidas, proponer cerrar perdido.
            Regla de "vencida" espejo del backend: borrador/enviada con
            vence_at en el pasado (estado_efectivo). */}
        {!cerrado && cotizaciones.length > 0 && cotizaciones.every(c => {
          const ef = (['borrador', 'enviada'].includes(c.estado) && c.vence_at && new Date(c.vence_at) < new Date())
            ? 'vencida' : c.estado;
          return ef === 'rechazada' || ef === 'vencida';
        }) && (
          <div style={{
            marginTop: 12, background: '#fffbeb', border: '1px solid #fde68a',
            borderRadius: 10, padding: '10px 14px',
            display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
          }}>
            <AlertTriangle size={16} color="#b45309" style={{ flexShrink: 0 }} />
            <span style={{ flex: 1, minWidth: 200, fontSize: 13, color: '#92400e', lineHeight: 1.45 }}>
              Todas las cotizaciones de este expediente fueron rechazadas o vencieron.
              {direccion ? ' ¿Cerrar como perdido?' : ' Avisale a direccion si ya no avanzara.'}
            </span>
            {direccion && (
              <button
                onClick={() => setModalCerrar('perdido')}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  background: '#fff', color: '#b45309', border: '1px solid #f59e0b',
                  borderRadius: 8, padding: '9px 14px', fontSize: 12.5, fontWeight: 700,
                  fontFamily: FF, cursor: 'pointer', minHeight: 44, flexShrink: 0,
                }}
              >Cerrar como perdido</button>
            )}
          </div>
        )}

        {/* TABS */}
        <div style={{
          marginTop: 16, display: 'flex', gap: 4, flexWrap: 'wrap',
          borderBottom: `1px solid ${COLOR_BORDE}`,
        }}>
          {TABS_VISIBLES.map(t => {
            const activo = tab === t.id;
            const sufijo = (t.id === 'vinculados' && Number(expediente.total_hijos || 0) > 0)
              ? ` (${expediente.total_hijos})` : '';
            return (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                style={{
                  background: 'transparent', border: 'none', cursor: 'pointer',
                  padding: '10px 14px',
                  fontSize: 13, fontFamily: FF, fontWeight: activo ? 700 : 500,
                  color: activo ? COLOR_BEEP_AZUL : COLOR_GRIS,
                  borderBottom: activo ? `2px solid ${COLOR_BEEP_AZUL}` : '2px solid transparent',
                  marginBottom: -1,
                }}
              >{t.label}{sufijo}</button>
            );
          })}
        </div>
      </div>

      {/* CONTENIDO DEL TAB */}
      <div style={{ padding: esCelular ? 14 : 24 }}>
        {tab === 'tareas'       && (
          /* PANEL-NAVEGADOR: el FLUJO ACTIVO arriba (linea de pasos = navegador)
             y debajo el PANEL UNICO del paso elegido. El contexto del proyecto
             (paso 1) se captura dentro de ese panel, sin ventanas. */
          <PanelFlujo
            expedienteId={expediente.id}
            esCelular={esCelular}
            direccion={direccion}
            verFinanzas={verFinanzas}
            cerradoManual={cerrado}
            expediente={expediente}
            historial={historial}
            verPasoReq={verPasoReq}
            onActualizado={() => { cargarExpediente(); }}
            onVerCotizaciones={() => setTab('cotizaciones')}
            onEditarExpediente={() => setModalEditar(true)}
          />
        )}
        {tab === 'resumen'      && (
          <TabResumen
            expediente={expediente}
            cotizaciones={cotizaciones}
            onCotizar={verFinanzas && !cerrado ? () => setPanelCotizar(true) : null}
            verFinanzas={verFinanzas}
            navegarA={navegarA}
            setTab={setTab}
            setModalReasignar={setModalReasignar}
            setModalVincular={setModalVincular}
            onAbrirContexto={abrirContexto}
          />
        )}
        {tab === 'contactos'    && <Placeholder icono={'\u{1F4DE}'} titulo="Contactos del expediente" />}
        {tab === 'cotizaciones' && (
          <div>
            <div style={{ fontSize: 12, color: COLOR_GRIS, marginBottom: 12, lineHeight: 1.4 }}>
              Estado de cada <strong>cotizacion</strong> (independiente del estado del expediente que aparece arriba).
            </div>
            {/* ESPEJO: mismo componente que el panel del paso Cotizacion. */}
            <CotizacionesLista oportunidadId={id} onCambio={() => cargarExpediente()} />
            {/* COTIZAR-RAPIDO Frente B: trabajo que ya se cotizo antes. Va DEBAJO de las
                 propias y plegado (COTIZACIONES-PARECIDAS). Si no hay ninguna, no pinta nada. */}
            <CotizacionesParecidas expedienteId={id} onCreada={() => cargarExpediente()} />
          </div>
        )}
        {tab === 'material'     && <TabMaterial expedienteId={id} esCelular={esCelular} />}
        {tab === 'servicios'    && <ServiciosExpediente expediente={expediente} esCelular={esCelular} />}
        {tab === 'equipos'      && <TabPuntos expedienteId={id} expediente={expediente} esCelular={esCelular} onCambio={() => {}} />}
        {tab === 'garantias'    && (
          <div>
            <div style={{ fontSize: 12, color: COLOR_GRIS, marginBottom: 12, lineHeight: 1.4 }}>
              Certificados de garantía de este expediente (CAPA 1: la verdad vive aquí). Arrancan en la fecha de entrega.
            </div>
            <GarantiasLista filtro={{ oportunidad_id: id }} />
            {/* PORTAL DEL CLIENTE (2026-09-08): pedirle sus datos fiscales y documentos desde el expediente. */}
            <div style={{ marginTop: 14 }}>
              <PedirDatosCard cliente={{ oportunidad_id: id, grupo_id: expediente.cliente_grupo_id, razon_social_id: expediente.cliente_razon_social_id, sucursal_id: expediente.cliente_sucursal_id, persona_id: expediente.cliente_persona_id, nombre: expediente.cliente_nombre || expediente.contacto_nombre || expediente.titulo || 'Cliente' }} />
            </div>
          </div>
        )}
        {tab === 'vinculados'   && <TabVinculados expediente={expediente} hijos={hijos} navegarA={navegarA} onAbrirCrearHijo={() => setModalCrearHijo(true)} />}
        {tab === 'historial'    && <TabHistorial expediente={expediente} historial={historial} eventos={eventos} recordatorios={recordatorios} />}
      </div>

      {/* MODALES */}
      {modalCerrar && (
        <ModalCerrarExpediente
          tipo={modalCerrar}
          expediente={expediente}
          onCerrar={() => setModalCerrar(null)}
          onConfirmado={() => tras_modal(true)}
          esCelular={esCelular}
        />
      )}
      {/* COTIZAR-RAPIDO: el panel de tres toques. Al crear, se recarga la ficha para que
           la cotizacion aparezca ya ligada sin tener que refrescar a mano. */}
      {panelCotizar && (
        <PanelCotizarRapido
          expedienteId={id}
          onCerrar={() => setPanelCotizar(false)}
          onCreada={() => cargarExpediente()}
        />
      )}
      {modalEditar && (
        <ModalEditarExpediente
          expediente={expediente}
          onCerrar={() => setModalEditar(false)}
          onGuardado={() => tras_modal(true)}
          esCelular={esCelular}
        />
      )}
      {modalReabrir && (
        <ModalReabrirExpediente
          expediente={expediente}
          onCerrar={() => setModalReabrir(false)}
          onConfirmado={() => tras_modal(true)}
          esCelular={esCelular}
        />
      )}
      {modalBorrar && (
        <ModalBorrarPermanente
          expediente={expediente}
          onCerrar={() => setModalBorrar(false)}
          onBorrado={tras_borrado}
          esCelular={esCelular}
        />
      )}
      {modalCrearHijo && (
        <ModalCrearHijo
          expedientePadre={expediente}
          onCerrar={() => setModalCrearHijo(false)}
          onCreado={() => { tras_modal(true); setTab('vinculados'); }}
          esCelular={esCelular}
        />
      )}
      {modalReasignar && (
        <ModalReasignar
          expediente={expediente}
          onCerrar={() => setModalReasignar(false)}
          onReasignado={() => tras_modal(true)}
          esCelular={esCelular}
        />
      )}
      {modalVincular && (
        <ModalVincularCliente
          expediente={expediente}
          onCerrar={() => setModalVincular(false)}
          onVinculado={() => tras_modal(true)}
          esCelular={esCelular}
        />
      )}
    </div>
  );
}

// ============================================
// TAB: Resumen
// ============================================
function TabResumen({ expediente, cotizaciones, navegarA, setTab, setModalReasignar, setModalVincular, onAbrirContexto, onCotizar = null, verFinanzas = false }) {
  // MULTI-INTERES (2026-10-17): los rubros del expediente, principal primero (misma cache del hook).
  const familiasCatR = useFamilias();
  const rubrosTexto = useMemo(() => {
    const lista = (Array.isArray(expediente.familias) && expediente.familias.length)
      ? expediente.familias : (expediente.familia ? [expediente.familia] : []);
    return lista.map(id => etiquetaFamilia(id, familiasCatR) || id).join(' + ');
  }, [expediente, familiasCatR]);
  // DOS-TIEMPOS: hay contexto capturado? (para el estado vacio de la card)
  const hayContexto = !!(
    expediente.necesidad || rubrosTexto || expediente.tipo_lugar || expediente.origen_lead ||
    expediente.descripcion_necesidad
  );
  const tieneClienteVinculado = !!(
    expediente.cliente_grupo_id || expediente.cliente_razon_social_id ||
    expediente.cliente_sucursal_id || expediente.cliente_persona_id
  );

  const inicialesResp = (expediente.responsable_nombre || '')
    .split(/\s+/).filter(Boolean).slice(0, 2)
    .map(p => p[0]?.toUpperCase()).join('') || '?';

  // Cotizacion mas reciente (las cotizaciones vienen ordenadas DESC por creado_at)
  const cotizacionVigente = cotizaciones && cotizaciones.length > 0 ? cotizaciones[0] : null;

  return (
    <div style={{ display: 'grid', gap: 14, gridTemplateColumns: 'minmax(0, 1fr)' }}>
      {/* Vinculacion al padre */}
      {expediente.expediente_padre_folio && (
        <div style={{
          background: '#fff7ed', border: '1px solid #fed7aa',
          borderRadius: 10, padding: '12px 14px',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8,
        }}>
          <div style={{ fontSize: 13, color: '#9a3412', fontWeight: 600 }}>
            {'↳ Este expediente esta vinculado a '}
            <span style={{
              fontFamily: 'ui-monospace, "SF Mono", Menlo, monospace',
              color: COLOR_BEEP_AZUL,
            }}>{expediente.expediente_padre_folio}</span>
          </div>
          <button
            onClick={() => navegarA(expediente.expediente_padre_id)}
            style={btnLink}
          >Abrir padre {'→'}</button>
        </div>
      )}

      {/* Hijos */}
      {Number(expediente.total_hijos || 0) > 0 && (
        <div style={{
          background: '#eef4fc', border: '1px solid #bfdbfe',
          borderRadius: 10, padding: '12px 14px',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8,
        }}>
          <div style={{ fontSize: 13, color: COLOR_BEEP_AZUL, fontWeight: 600 }}>
            Tiene {expediente.total_hijos} expediente{Number(expediente.total_hijos) === 1 ? '' : 's'} vinculado{Number(expediente.total_hijos) === 1 ? '' : 's'}.
          </div>
          <button onClick={() => setTab('vinculados')} style={btnLink}>
            Ver vinculados {'→'}
          </button>
        </div>
      )}

      {/* Card: CONTEXTO DEL INTERES (DOS-TIEMPOS). Los expedientes viejos
          conservan sus datos aqui; los nuevos lo capturan por rondas. */}
      <Card
        titulo="Contexto del interes"
        accion={(
          <button
            onClick={onAbrirContexto}
            // Override tactil: btnLink mide ~22px de alto; este boton es NUEVO y
            // de uso frecuente en celular -> 44px (hallazgo medido de Don Porfirio).
            style={{ ...btnLink, minHeight: 44, display: 'inline-flex', alignItems: 'center', padding: '10px 10px' }}
          >
            {hayContexto ? 'Editar contexto' : 'Agregar contexto'}
          </button>
        )}
      >
        {hayContexto ? (
          <>
            {rubrosTexto
              ? <Linea label="Rubros" valor={rubrosTexto} />
              : <Linea label="Tipo de necesidad" valor={expediente.necesidad_nombre || '—'} icono={expediente.necesidad_icono} />}
            <Linea label="Tipo de lugar"     valor={expediente.tipo_lugar_nombre || '—'} icono={expediente.tipo_lugar_icono} />
            <Linea label="Como llego"        valor={expediente.origen_nombre || '—'} icono={expediente.origen_icono} />
            {expediente.origen_detalle && <Linea label="Detalle origen" valor={expediente.origen_detalle} />}
            {expediente.descripcion_necesidad && (
              <div style={{ marginTop: 8 }}>
                <div style={{ fontSize: 11, color: COLOR_GRIS_CL, fontWeight: 600, marginBottom: 4 }}>
                  Descripcion de la necesidad
                </div>
                <div style={{
                  fontSize: 13, color: COLOR_OSCURO, lineHeight: 1.5,
                  background: COLOR_FONDO, padding: 10, borderRadius: 8,
                  whiteSpace: 'pre-wrap',
                }}>{expediente.descripcion_necesidad}</div>
              </div>
            )}
          </>
        ) : (
          <div style={{ fontSize: 13, color: COLOR_GRIS_CL, lineHeight: 1.5 }}>
            Sin contexto capturado. Con "Agregar contexto" respondes, por rondas
            cortas, como llego el cliente y que necesita (nada es obligatorio).
          </div>
        )}
      </Card>

      {/* Notas destacadas — solo si existen */}
      {expediente.notas && expediente.notas.trim() && (
        <NotasDestacadas notas={expediente.notas} />
      )}

      {/* Card: cotizacion */}
      <CardCotizacion cotizacion={cotizacionVigente} onCotizar={onCotizar} />

      {/* Card: cliente */}
      <Card
        titulo="Cliente vinculado"
        accion={tieneClienteVinculado ? (
          <button onClick={() => setModalVincular(true)} style={btnLink}>
            Cambiar
          </button>
        ) : null}
      >
        {tieneClienteVinculado ? (
          <ClienteVinculado expediente={expediente} />
        ) : (
          <ClienteVacio
            expediente={expediente}
            onVincular={() => setModalVincular(true)}
          />
        )}
      </Card>

      {/* Card: responsable */}
      <Card titulo="Responsable">
        {expediente.responsable_nombre ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{
                width: 36, height: 36, borderRadius: '50%',
                background: COLOR_BEEP_AZUL, color: '#fff',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 13, fontWeight: 700,
              }}>{inicialesResp}</span>
              <div>
                <div style={{ fontSize: 14, fontWeight: 700, color: COLOR_OSCURO }}>{expediente.responsable_nombre}</div>
                <div style={{ fontSize: 11, color: COLOR_GRIS_CL }}>Vendedor asignado</div>
              </div>
            </div>
            <button onClick={() => setModalReasignar(true)} style={btnLink}>Reasignar</button>
          </div>
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
            <div style={{ fontSize: 13, color: COLOR_GRIS_CL, fontStyle: 'italic' }}>
              Sin responsable asignado.
            </div>
            <button onClick={() => setModalReasignar(true)} style={btnLink}>Asignar</button>
          </div>
        )}
      </Card>
    </div>
  );
}

// ============================================
// Card destacada: Notas del primer contacto
// ============================================
function NotasDestacadas({ notas }) {
  return (
    <div style={{
      background: '#fef3c7', borderLeft: '4px solid #f59e0b',
      border: '1px solid #fcd34d', borderRadius: 10,
      padding: '14px 18px',
    }}>
      <div style={{
        fontSize: 11, fontWeight: 700, color: '#92400e',
        textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8,
        fontFamily: FF,
      }}>Notas del primer contacto</div>
      <div style={{
        fontSize: 14, color: '#78350f', lineHeight: 1.55,
        whiteSpace: 'pre-wrap', fontFamily: FF,
      }}>{notas}</div>
    </div>
  );
}

// ============================================
// Card: Cotizacion vinculada
// ============================================
function CardCotizacion({ cotizacion, onCotizar = null }) {
  if (!cotizacion) {
    return (
      <Card titulo="Cotizacion">
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          gap: 12, flexWrap: 'wrap',
        }}>
          <div style={{ fontSize: 13, color: COLOR_GRIS_CL, fontStyle: 'italic' }}>
            Aun sin cotizacion vinculada.
          </div>
          {/* COTIZAR-RAPIDO (2026-09-23): este boton llevaba `disabled` con el texto
               "Disponible cuando se integre el cotizador" desde que se escribio la ficha.
               Ya esta integrado. */}
          <button
            onClick={() => onCotizar && onCotizar()}
            disabled={!onCotizar}
            title={onCotizar ? 'Crear una cotizacion ligada a este expediente' : 'No tienes permiso para cotizar'}
            style={{
              background: onCotizar ? '#1b9e6d' : '#e5e7eb',
              color: onCotizar ? '#fff' : COLOR_GRIS_CL, border: 'none',
              padding: '10px 14px', minHeight: 40, borderRadius: 8,
              fontSize: 12, fontWeight: 700, fontFamily: FF,
              cursor: onCotizar ? 'pointer' : 'not-allowed',
            }}
          >+ Crear cotizacion</button>
        </div>
      </Card>
    );
  }

  const total = fmtMoneda(cotizacion.total);
  const estadoColor = {
    borrador:  '#9ca3af',
    enviada:   COLOR_BEEP_AZUL,
    aprobada:  COLOR_OK,
    rechazada: COLOR_PELIGRO,
  }[cotizacion.estado] || COLOR_GRIS;

  return (
    <Card titulo="Cotizacion">
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        gap: 12, flexWrap: 'wrap',
      }}>
        <div style={{ minWidth: 0 }}>
          <div style={{
            fontFamily: 'ui-monospace, "SF Mono", Menlo, monospace',
            fontSize: 13, fontWeight: 700, color: COLOR_OSCURO,
          }}>{cotizacion.folio}</div>
          <div style={{
            fontSize: 11, color: estadoColor, fontWeight: 700,
            textTransform: 'uppercase', letterSpacing: 0.5, marginTop: 2,
          }}>{cotizacion.estado || ''}</div>
          {total && (
            <div style={{
              fontSize: 16, fontWeight: 700, color: COLOR_OSCURO, marginTop: 6,
            }}>{total}</div>
          )}
        </div>
        <button
          onClick={() => window.open(`/cotizaciones/${cotizacion.id}`, '_blank')}
          style={btnLink}
        >Ver cotizacion {'→'}</button>
      </div>
    </Card>
  );
}

// ============================================
// Cliente vinculado: render enriquecido
// ============================================
// Linea 1 (negrita grande): grupo
// Linea 2 (gris medio):     razon social
// Linea 3 (gris medio):     "Sucursal: <nombre>"
// Linea 4 (pequeña):        direccion completa de la sucursal (si existe)
// Linea 5 (pequeña):        persona contacto si existe
function ClienteVinculado({ expediente }) {
  // EXP-CLIENTE-CUALQUIERA: la persona es el CLIENTE principal cuando no hay
  // grupo/razon/sucursal; en ese caso se muestra como titular (no como contacto)
  // y la direccion del proyecto se toma de la persona.
  const personaEsCliente = !!expediente.cliente_persona_nombre &&
    !expediente.cliente_grupo_nombre &&
    !expediente.cliente_razon_social_nombre &&
    !expediente.cliente_sucursal_nombre;

  const direccionSucursal = [
    expediente.cliente_sucursal_direccion,
    expediente.cliente_sucursal_ciudad,
    expediente.cliente_sucursal_estado_republica,
  ].filter(Boolean).join(', ');

  const direccionPersona = [
    expediente.cliente_persona_direccion,
    expediente.cliente_persona_ciudad,
    expediente.cliente_persona_estado_republica,
  ].filter(Boolean).join(', ');

  // La sucursal manda; si no hay (persona/empresa), usamos la de la persona.
  const direccionCompleta = direccionSucursal || direccionPersona;

  const contactoPersona = [
    expediente.cliente_persona_celular || expediente.cliente_persona_telefono,
    expediente.cliente_persona_email,
  ].filter(Boolean).join(' · ');

  const personaInfo = expediente.cliente_persona_nombre
    ? (contactoPersona
        ? `${expediente.cliente_persona_nombre} - ${contactoPersona}`
        : expediente.cliente_persona_nombre)
    : null;

  // Nombre principal (titular): grupo -> razon social -> persona (si es cliente).
  const titular = expediente.cliente_grupo_nombre
    || expediente.cliente_razon_social_nombre
    || (personaEsCliente ? expediente.cliente_persona_nombre : null);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      {titular && (
        <div style={{
          fontSize: 17, fontWeight: 700, color: COLOR_OSCURO, lineHeight: 1.3,
        }}>{titular}</div>
      )}
      {/* Razon social como linea secundaria solo si NO es el titular (hay grupo) */}
      {expediente.cliente_razon_social_nombre && expediente.cliente_grupo_nombre && (
        <div style={{ fontSize: 13, color: COLOR_GRIS, lineHeight: 1.4 }}>
          {expediente.cliente_razon_social_nombre}
        </div>
      )}
      {expediente.cliente_sucursal_nombre && (
        <div style={{ fontSize: 13, color: COLOR_GRIS, lineHeight: 1.4 }}>
          <span style={{ color: COLOR_GRIS_CL, marginRight: 4 }}>Sucursal:</span>
          <span style={{ fontWeight: 600 }}>{expediente.cliente_sucursal_nombre}</span>
        </div>
      )}
      {/* EXP-UBICACION: sin sucursal, mostrar la ubicacion libre capturada. */}
      {!expediente.cliente_sucursal_nombre && textoUbicacionLibre(expediente) && (
        <div style={{ fontSize: 13, color: COLOR_GRIS, lineHeight: 1.4 }}>
          <span style={{ color: COLOR_GRIS_CL, marginRight: 4 }}>Ubicacion:</span>
          <span style={{ fontWeight: 600 }}>{textoUbicacionLibre(expediente)}</span>
        </div>
      )}
      {direccionCompleta && (
        <div style={{
          fontSize: 12, color: COLOR_GRIS_CL, lineHeight: 1.5,
          marginTop: 2,
        }}>{direccionCompleta}</div>
      )}
      {/* Persona como CONTACTO solo si no es ella misma el cliente titular */}
      {personaInfo && !personaEsCliente && (
        <div style={{
          fontSize: 12, color: COLOR_GRIS_CL, lineHeight: 1.5,
          marginTop: 2,
        }}>
          <span style={{ marginRight: 4 }}>Contacto:</span>
          <span>{personaInfo}</span>
        </div>
      )}
      {/* Si la persona ES el cliente, mostrar su contacto bajo el titular */}
      {personaEsCliente && contactoPersona && (
        <div style={{
          fontSize: 12, color: COLOR_GRIS_CL, lineHeight: 1.5,
          marginTop: 2,
        }}>{contactoPersona}</div>
      )}
    </div>
  );
}

// ============================================
// Cliente vacio: prompt para vincular
// ============================================
function ClienteVacio({ expediente, onVincular }) {
  const tieneContactoLegacy = !!(
    expediente.contacto_nombre || expediente.contacto_telefono ||
    expediente.contacto_email || expediente.contacto_empresa
  );
  return (
    <div style={{
      display: 'flex', flexDirection: 'column', gap: 10,
    }}>
      {tieneContactoLegacy && (
        <div style={{
          background: COLOR_FONDO, border: `1px dashed ${COLOR_BORDE}`,
          borderRadius: 8, padding: 10,
        }}>
          <div style={{
            fontSize: 11, color: COLOR_GRIS_CL, fontWeight: 600,
            textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6,
          }}>Contacto capturado al crear</div>
          {expediente.contacto_nombre   && <Linea label="Nombre"   valor={expediente.contacto_nombre} />}
          {expediente.contacto_telefono && <Linea label="Telefono" valor={expediente.contacto_telefono} />}
          {expediente.contacto_email    && <Linea label="Email"    valor={expediente.contacto_email} />}
          {expediente.contacto_empresa  && <Linea label="Empresa"  valor={expediente.contacto_empresa} />}
        </div>
      )}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        gap: 10, flexWrap: 'wrap',
      }}>
        <div style={{ fontSize: 13, color: COLOR_GRIS_CL, fontStyle: 'italic' }}>
          {tieneContactoLegacy
            ? 'Aun no se ha vinculado a un cliente del directorio.'
            : 'Este expediente todavia no tiene cliente vinculado.'}
        </div>
        <button
          onClick={onVincular}
          style={{
            background: COLOR_OK, color: '#fff', border: 'none',
            padding: '8px 16px', borderRadius: 8,
            fontSize: 13, fontWeight: 700, fontFamily: FF, cursor: 'pointer',
            boxShadow: '0 2px 6px rgba(29,158,117,0.25)',
          }}
        >Vincular cliente</button>
      </div>
    </div>
  );
}

// ============================================
// TAB: Vinculados
// ============================================
function TabVinculados({ expediente, hijos, navegarA, onAbrirCrearHijo }) {
  return (
    <div style={{ display: 'grid', gap: 14 }}>
      {expediente.expediente_padre_folio && (
        <Card titulo="Padre">
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            gap: 8, flexWrap: 'wrap',
          }}>
            <div>
              <div style={{
                fontFamily: 'ui-monospace, "SF Mono", Menlo, monospace',
                fontSize: 13, fontWeight: 700, color: COLOR_BEEP_AZUL,
              }}>{expediente.expediente_padre_folio}</div>
              <div style={{ fontSize: 11, color: COLOR_GRIS_CL, marginTop: 2 }}>
                Expediente del que hereda este registro
              </div>
            </div>
            <button onClick={() => navegarA(expediente.expediente_padre_id)} style={btnLink}>
              Abrir padre {'→'}
            </button>
          </div>
        </Card>
      )}

      <Card
        titulo="Hijos vinculados"
        accion={(
          <button onClick={onAbrirCrearHijo} style={{
            background: '#fff', color: COLOR_BEEP_AZUL,
            border: `1px solid ${COLOR_BEEP_AZUL}`,
            padding: '6px 12px', borderRadius: 8,
            fontSize: 12, fontWeight: 700, fontFamily: FF, cursor: 'pointer',
          }}>+ Crear vinculado</button>
        )}
      >
        {hijos.length === 0 ? (
          <div style={{ fontSize: 13, color: COLOR_GRIS_CL, fontStyle: 'italic' }}>
            Sin expedientes hijo. Crea uno para registrar anexos, garantias o servicios postventa.
          </div>
        ) : (
          <div style={{ display: 'grid', gap: 8 }}>
            {hijos.map(h => {
              const tv = TIPO_EXPEDIENTE_VISUAL[h.tipo_expediente];
              const ev = ESTADO_VISUAL_OPORTUNIDAD[h.estado];
              return (
                <div
                  key={h.id}
                  onClick={() => navegarA(h.id)}
                  style={{
                    background: '#fff', border: `1px solid ${COLOR_BORDE}`, borderRadius: 10,
                    padding: 12, cursor: 'pointer', transition: 'border-color 0.15s',
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    gap: 10, flexWrap: 'wrap',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.borderColor = COLOR_BEEP_AZUL; }}
                  onMouseLeave={(e) => { e.currentTarget.style.borderColor = COLOR_BORDE; }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <span style={{
                      fontFamily: 'ui-monospace, "SF Mono", Menlo, monospace',
                      fontSize: 12, fontWeight: 700, color: COLOR_GRIS,
                    }}>{h.folio}</span>
                    {tv && <Badge color={tv.color} label={`${tv.icono} ${tv.label}`} tooltip={tv.descripcion} />}
                    {ev && <Badge color={ev.color} label={ev.label} tooltip={ev.descripcion} />}
                  </div>
                  <div style={{ fontSize: 12, color: COLOR_GRIS_CL }}>
                    {h.responsable_nombre || ''}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Card>
    </div>
  );
}

// ============================================
// TAB: Historial — timeline minimalista
// ============================================
function TabHistorial({ expediente, historial, eventos = [], recordatorios = [] }) {
  // TRANSPARENCIA-TOTAL: UN solo timeline legible con QUIEN + CUANDO. Fusiona
  // avances de paso, reasignaciones, ediciones, recordatorios y el cierre. Todo
  // ordenado del mas reciente al mas antiguo.
  const items = [];

  // Avances de paso (oportunidad_historial; ya trae usuario_nombre + etiquetas).
  // Un regreso de paso viaja con nota [REGRESO] <motivo> y se lee distinto.
  for (const h of historial) {
    const quien = etiquetaAutor(h.usuario_nombre, h.nota);
    const destino = h.estado_nuevo_nombre || h.estado_nuevo;
    const esRegreso = typeof h.nota === 'string' && h.nota.startsWith('[REGRESO]');
    const esSalto = typeof h.nota === 'string' && h.nota.startsWith('[SALTO]');
    items.push({
      key: `h-${h.id}`,
      fecha: h.fecha,
      texto: esRegreso
        ? `${quien} regreso el expediente a ${destino}`
        : (esSalto
          ? `${quien} salto directo a ${destino}`
          : (h.estado_anterior
            ? `${quien} avanzo a ${destino}`
            : `${quien} creo el expediente en ${destino}`)),
      nota: esRegreso
        ? `Motivo: ${h.nota.replace('[REGRESO]', '').trim()}`
        : (esSalto ? h.nota.replace('[SALTO]', '').trim() : (h.nota || null)),
      color: esRegreso ? '#d97706' : (esSalto ? '#b45309' : '#185FA5'),
      icono: esRegreso ? '↩' : (esSalto ? '↷' : '→'),
    });
  }
  // Reasignaciones, ediciones, impresiones y ediciones retroactivas
  // (expediente_paso_eventos). La edicion retroactiva es la marca de
  // supervision: se corrigio informacion de un paso YA completado.
  for (const ev of eventos) {
    const quien = etiquetaAutor(ev.usuario_nombre, ev.nota);
    const esReasig = ev.registro_tipo === 'reasignacion';
    const esImpresion = ev.registro_tipo === 'impresion_portada';
    const esRetro = ev.registro_tipo === 'edicion_retroactiva';
    const accion = ev.nota
      ? `${ev.nota.charAt(0).toLowerCase()}${ev.nota.slice(1)}`
      : (esReasig ? 'reasigno el expediente' : (esImpresion ? 'imprimio la portada del expediente' : 'edito el expediente'));
    items.push({
      key: `e-${ev.id}`,
      fecha: ev.creado_at,
      texto: `${quien} ${accion}`,
      color: esReasig ? '#a855f7' : (esImpresion ? '#0ea5e9' : (esRetro ? '#d97706' : '#6b7280')),
      icono: esReasig ? '⇄' : (esImpresion ? '⎙' : '✎'),
    });
  }
  // Recordatorios al responsable.
  for (const rec of recordatorios) {
    items.push({
      key: `r-${rec.id}`,
      fecha: rec.creado_at,
      texto: rec.usuario_nombre ? `${rec.usuario_nombre} aviso al responsable` : 'Aviso al responsable',
      nota: rec.nota || null,
      color: '#dc2626', icono: '✉',
    });
  }
  // Cierre (evento sintetico con su detalle).
  if (expediente.fecha_cierre && expediente.tipo_cierre) {
    const cv = CIERRE_VISUAL[expediente.tipo_cierre];
    items.push({
      key: 'cierre',
      fecha: expediente.fecha_cierre,
      texto: `${expediente.cerrado_por_nombre || 'Direccion'} cerro como ${cv?.label || expediente.tipo_cierre}`,
      nota: [
        expediente.razon_cierre ? `Razon: ${expediente.razon_cierre}` : null,
        expediente.nota_cierre ? `Nota: ${expediente.nota_cierre}` : null,
      ].filter(Boolean).join(' · ') || null,
      color: cv?.color || '#6b7280', icono: '●',
    });
  }

  items.sort((a, b) => new Date(b.fecha || 0) - new Date(a.fecha || 0));

  return (
    <Card titulo="Historial">
      {items.length === 0 ? (
        <div style={{ fontSize: 13, color: COLOR_GRIS_CL, textAlign: 'center', padding: '20px 0' }}>
          Aun no hay movimientos registrados.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {items.map(e => (
            <div key={e.key} style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
              <span style={{ color: e.color || COLOR_GRIS_CL, fontSize: 14, marginTop: 1, width: 16, textAlign: 'center', flexShrink: 0 }}>{e.icono}</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: COLOR_OSCURO, lineHeight: 1.35 }}>{e.texto}</div>
                <div style={{ fontSize: 11, color: COLOR_GRIS_CL, marginTop: 2 }}>{fmtFecha(e.fecha)}</div>
                {e.nota && <div style={{ fontSize: 12, color: COLOR_GRIS, marginTop: 3, lineHeight: 1.4 }}>{e.nota}</div>}
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

// ============================================
// HELPERS DE UI
// ============================================
function Card({ titulo, accion, children }) {
  return (
    <div style={{
      background: '#fff', border: `1px solid ${COLOR_BORDE}`,
      borderRadius: 10, padding: 16,
    }}>
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        marginBottom: 12, gap: 8,
      }}>
        <h3 style={{
          margin: 0, fontSize: 13, fontWeight: 700, color: COLOR_OSCURO,
          textTransform: 'uppercase', letterSpacing: 0.5, fontFamily: FF,
        }}>{titulo}</h3>
        {accion}
      </div>
      {children}
    </div>
  );
}

function Linea({ label, valor, icono }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'baseline', gap: 8,
      padding: '6px 0', borderBottom: `1px dashed ${COLOR_BORDE}`,
    }}>
      <span style={{ fontSize: 11, color: COLOR_GRIS_CL, fontWeight: 600, minWidth: 140 }}>
        {label}
      </span>
      <span style={{ fontSize: 13, color: COLOR_OSCURO, flex: 1 }}>
        {icono && <span style={{ marginRight: 4 }}>{icono}</span>}
        {valor}
      </span>
    </div>
  );
}

function Badge({ color, label, tooltip }) {
  return (
    <span
      title={tooltip}
      style={{
        background: color + '22', color,
        padding: '3px 8px', borderRadius: 99,
        fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4,
        whiteSpace: 'nowrap',
      }}
    >{label}</span>
  );
}

function Placeholder({ icono, titulo }) {
  return (
    <div style={{
      padding: 60, textAlign: 'center',
      background: '#fff', borderRadius: 10, border: `1px dashed ${COLOR_BORDE}`,
    }}>
      <div style={{ fontSize: 48, marginBottom: 12, opacity: 0.5 }}>{icono}</div>
      <div style={{ fontSize: 16, fontWeight: 600, color: COLOR_OSCURO, marginBottom: 4 }}>{titulo}</div>
      <div style={{ fontSize: 13, color: COLOR_GRIS_CL }}>Proximamente</div>
      <div style={{ fontSize: 11, color: COLOR_GRIS_CL, marginTop: 4, fontStyle: 'italic' }}>
        Disponible en proxima fase
      </div>
    </div>
  );
}

const btnVolver = {
  background: 'transparent', border: 'none', color: COLOR_BEEP_AZUL,
  fontSize: 13, fontWeight: 600, cursor: 'pointer', padding: 0, fontFamily: FF,
};

// CABECERA-LIMPIA: menu contextual ⋮ con las acciones discretas de cierre.
// Solo direccion tiene items (mismos candados de siempre); sin items no se
// renderiza. Cada item abre su modal existente (confirmacion + motivo firmado).
function MenuAcciones({ direccion, cerrado, tipoCierre, onCerrarPerdido, onDescartar, onReabrir, onBorrar }) {
  const [abierto, setAbierto] = useState(false);
  const ref = useRef(null);

  const items = [];
  if (direccion && !cerrado) {
    items.push({ id: 'perdido', label: 'Cerrar como perdido', Icono: XCircle, color: COLOR_PELIGRO, onClick: onCerrarPerdido });
    items.push({ id: 'descartar', label: 'Descartar expediente', Icono: Archive, color: '#6b7280', onClick: onDescartar });
  }
  if (direccion && (tipoCierre === 'ganado' || tipoCierre === 'perdido')) {
    items.push({ id: 'reabrir', label: 'Reabrir expediente', Icono: Undo2, color: COLOR_BEEP_AZUL, onClick: onReabrir });
  }
  if (direccion && tipoCierre === 'descartado') {
    items.push({ id: 'reabrir', label: 'Reabrir expediente', Icono: Undo2, color: COLOR_BEEP_AZUL, onClick: onReabrir });
    items.push({ id: 'borrar', label: 'Borrar permanente', Icono: Trash2, color: COLOR_PELIGRO, onClick: onBorrar });
  }

  useEffect(() => {
    if (!abierto) return;
    const cerrar = (e) => { if (ref.current && !ref.current.contains(e.target)) setAbierto(false); };
    const esc = (e) => { if (e.key === 'Escape') setAbierto(false); };
    document.addEventListener('mousedown', cerrar);
    document.addEventListener('keydown', esc);
    return () => { document.removeEventListener('mousedown', cerrar); document.removeEventListener('keydown', esc); };
  }, [abierto]);

  if (items.length === 0) return null;

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        onClick={() => setAbierto(a => !a)}
        aria-label="Mas acciones"
        title="Mas acciones"
        style={{
          ...btnAccion(COLOR_GRIS), display: 'inline-flex', alignItems: 'center',
          justifyContent: 'center', minWidth: 44, padding: '8px 10px',
        }}
      ><MoreVertical size={17} /></button>
      {abierto && (
        <div style={{
          position: 'absolute', right: 0, top: 'calc(100% + 6px)', zIndex: 300,
          background: '#fff', border: `1px solid ${COLOR_BORDE}`, borderRadius: 10,
          boxShadow: '0 10px 28px rgba(0,0,0,0.14)', minWidth: 210, padding: 6,
        }}>
          {items.map(it => (
            <button
              key={it.id + it.label}
              onClick={() => { setAbierto(false); it.onClick(); }}
              style={{
                display: 'flex', alignItems: 'center', gap: 8, width: '100%',
                background: 'transparent', border: 'none', cursor: 'pointer',
                padding: '10px 10px', minHeight: 44, borderRadius: 8, textAlign: 'left',
                fontSize: 13, fontWeight: 600, fontFamily: FF, color: it.color,
                WebkitTapHighlightColor: 'transparent',
              }}
            >
              <it.Icono size={15} style={{ flexShrink: 0 }} />
              {it.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function btnAccion(color) {
  return {
    background: '#fff', color, border: `1px solid ${color}`,
    padding: '8px 14px', borderRadius: 8,
    fontSize: 12, fontWeight: 700, fontFamily: FF, cursor: 'pointer',
    transition: 'background 0.15s',
    // 44px = minimo tactil del proyecto; uniforme en TODA la fila de acciones
    // (hallazgo Dona Frida 2026-07-09: fila con "escalon" de 44 vs 32px).
    minHeight: 44,
  };
}

const btnLink = {
  background: 'transparent', border: 'none',
  color: COLOR_BEEP_AZUL, fontSize: 12, fontWeight: 700,
  cursor: 'pointer', fontFamily: FF, padding: '4px 8px',
};
